import {
  DEFAULT_PRODUCT_LISTING_FILTER_STATE,
  queryToProductListingState,
  productListingStateToQuery,
} from '~~/helpers/productListing/productListingQueryState';
import {
  PRODUCT_LISTING_REFINEMENT_OPERATOR,
  PRODUCT_LISTING_REFINEMENT_OPERATOR_SYMBOL,
  CT_SEARCH_FILTERS,
  CT_SEARCH_FILTER_KIND,
} from '~~/constants/commercetoolsProductSearch';
import type {
  ProductListingRefinementOperator,
  CtSearchFilter,
  CtSearchRefinementListFilterName,
} from '~~/constants/commercetoolsProductSearch';
import type {
  ProductListingFilterState,
  ProductListingPriceRange,
  ProductListingRefinementList,
} from '~~/types/productListing/ProductListingFilterState';

export interface ProductListingCurrentRefinement {
  attribute: string;
  label: string;
  value: string;
  operator?: ProductListingRefinementOperator;
  remove: () => void;
}

type ProductListingQuery = Record<string, string>;

interface UseProductListingFiltersParams {
  defaultFilters?: ProductListingRefinementList;
  syncWithUrl?: boolean;
}

// Without URL sync (CMS listings without enableStateMapping, like Typesense without a router) the
// state stays in memory, so several listings on one page don't overwrite each other's filters.
function useProductListingQuerySource(syncWithUrl: boolean) {

  const route = useRoute();
  const router = useRouter();
  const localQuery = ref<ProductListingQuery>({});

  const listingQuery = computed(() => (syncWithUrl ? route.query : localQuery.value) as Record<string, unknown>);

  // A history entry per change, so Back undoes the last filter, sort or page change.
  const pushQuery = async (nextQuery: ProductListingQuery) => {

    if (syncWithUrl) {

      await router.push({ query: nextQuery });

      return;

    }

    localQuery.value = nextQuery;

  };

  const getQueryHref = (nextQuery: ProductListingQuery) =>
    (syncWithUrl ? router.resolve({ query: nextQuery }).href : '#');

  return { listingQuery, pushQuery, getQueryHref };

}

// URL <-> filter state for the CT listings, with the same query keys as Typesense so URLs keep
// working. `defaultFilters` (CMS preFilter) apply until the user removes them.
export function useProductListingFilters(
  { defaultFilters = {}, syncWithUrl = true }: UseProductListingFiltersParams = {},
) {

  const { listingQuery, pushQuery, getQueryHref } = useProductListingQuerySource(syncWithUrl);

  const filterState = computed<ProductListingFilterState>(
    () => queryToProductListingState(listingQuery.value, defaultFilters),
  );

  const toQuery = (state: ProductListingFilterState) => productListingStateToQuery(state, defaultFilters);

  const updateFilterState = (
    patch: Partial<ProductListingFilterState>,
    { resetPage = true }: { resetPage?: boolean } = {},
  ) => {

    const nextState: ProductListingFilterState = {
      ...filterState.value,
      ...patch,
      page: resetPage && !('page' in patch) ? 1 : (patch.page ?? filterState.value.page),
    };

    return pushQuery(toQuery(nextState));

  };

  const toggleRefinement = (attribute: CtSearchRefinementListFilterName, value: string) => {

    const current = filterState.value.refinementList[attribute] || [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];

    return updateFilterState({
      refinementList: { ...filterState.value.refinementList, [attribute]: next },
    });

  };

  const setPriceRange = (range: { min?: number; max?: number } | undefined) =>
    updateFilterState({ range: { price: range } });

  const setRating = (value: number | undefined) => {

    const isAlreadySelected = value === filterState.value.ratingMenu.avgRating;

    return updateFilterState({ ratingMenu: { avgRating: isAlreadySelected ? undefined : value } });

  };

  // One category id is enough (categoriesSubTree includes descendants); reselecting clears it.
  const setCategory = (categoryId: string | undefined) =>
    updateFilterState({ category: categoryId === filterState.value.category ? undefined : categoryId });

  const setSort = (sortBy: string) => updateFilterState({ sortBy });

  const setQuery = (query: string) => updateFilterState({ query });

  const setPage = (page: number) => updateFilterState({ page }, { resetPage: false });

  const getPageUrl = (page: number) => getQueryHref(toQuery({ ...filterState.value, page }));

  const setHitsPerPage = (hitsPerPage: number) => updateFilterState({ hitsPerPage });

  // Like ais-clear-refinements: only refinements are cleared; the search text, sort and page size stay.
  const clearAll = () => {

    const { query, sortBy, hitsPerPage } = filterState.value;

    return pushQuery(toQuery({ ...DEFAULT_PRODUCT_LISTING_FILTER_STATE, query, sortBy, hitsPerPage }));

  };

  const getOperatorLabel = (operator: ProductListingRefinementOperator, value: number) =>
    `${PRODUCT_LISTING_REFINEMENT_OPERATOR_SYMBOL[operator]} ${value}`;

  // One chip per price bound, like InstantSearch's numeric refinements.
  const getPriceRefinements = (priceRange?: ProductListingPriceRange): ProductListingCurrentRefinement[] => {

    const { min, max } = priceRange ?? {};
    const refinements: ProductListingCurrentRefinement[] = [];

    if (min !== undefined) {

      refinements.push({
        attribute: 'price',
        label: getOperatorLabel(PRODUCT_LISTING_REFINEMENT_OPERATOR.GTE, min),
        value: String(min),
        operator: PRODUCT_LISTING_REFINEMENT_OPERATOR.GTE,
        remove: () => setPriceRange(max === undefined ? undefined : { max }),
      });

    }

    if (max !== undefined) {

      refinements.push({
        attribute: 'price',
        label: getOperatorLabel(PRODUCT_LISTING_REFINEMENT_OPERATOR.LTE, max),
        value: String(max),
        operator: PRODUCT_LISTING_REFINEMENT_OPERATOR.LTE,
        remove: () => setPriceRange(min === undefined ? undefined : { min }),
      });

    }

    return refinements;

  };

  const getRatingRefinements = (avgRating?: number): ProductListingCurrentRefinement[] => {

    if (avgRating === undefined) {

      return [];

    }

    return [{
      attribute: 'avgRating',
      label: getOperatorLabel(PRODUCT_LISTING_REFINEMENT_OPERATOR.GTE, avgRating),
      value: String(avgRating),
      operator: PRODUCT_LISTING_REFINEMENT_OPERATOR.GTE,
      remove: () => setRating(undefined),
    }];

  };

  const getRefinementListRefinements = (
    attribute: CtSearchRefinementListFilterName,
    values: string[] = [],
  ): ProductListingCurrentRefinement[] => values.map(value => ({
    attribute,
    label: value,
    value,
    remove: () => toggleRefinement(attribute, value),
  }));

  const getFilterRefinements = (
    filter: CtSearchFilter,
    state: ProductListingFilterState,
  ): ProductListingCurrentRefinement[] => {

    switch (filter.kind) {

      case CT_SEARCH_FILTER_KIND.REFINEMENT_LIST:

        return getRefinementListRefinements(filter.name, state.refinementList[filter.name]);

      case CT_SEARCH_FILTER_KIND.PRICE:

        return getPriceRefinements(state.range.price);

      case CT_SEARCH_FILTER_KIND.RATING:

        return getRatingRefinements(state.ratingMenu.avgRating);

    }

  };

  // Chips in sidebar order.
  const currentRefinements = computed<ProductListingCurrentRefinement[]>(() =>
    CT_SEARCH_FILTERS.flatMap(filter => getFilterRefinements(filter, filterState.value)));

  return {
    filterState,
    updateFilterState,
    toggleRefinement,
    setPriceRange,
    setRating,
    setCategory,
    setSort,
    setQuery,
    setPage,
    getPageUrl,
    setHitsPerPage,
    clearAll,
    currentRefinements,
  };

}
